import type {
  MonthlyWarehouseMetric,
  PicqerAnalyseCache,
  PicqerLocation,
  PicqerLocationStockHistoryEntry,
  PicqerWarehouse,
} from './types';
import { ANALYSE_SINCE_DATE, ANALYSE_UNTIL_DATE } from './types';

export interface PicqerAnalyseExport extends PicqerAnalyseCache {
  exportedAt: string;
  warehouses: PicqerWarehouse[];
  /** Snapshot stage when exported */
  exportStage?: 'warehouses' | 'locations' | 'complete';
}

function numRecord<T>(raw: Record<string, T>): Record<number, T> {
  const out: Record<number, T> = {};
  for (const [key, value] of Object.entries(raw)) {
    out[Number(key)] = value;
  }
  return out;
}

export function buildExportPayload(
  warehouses: PicqerWarehouse[],
  locationsByWarehouse: Record<number, PicqerLocation[]>,
  historyByLocation: Record<number, PicqerLocationStockHistoryEntry[]>,
  preRangeStockByLocation: Record<number, number>,
): PicqerAnalyseExport {
  const locationCount = Object.values(locationsByWarehouse).reduce((sum, locs) => sum + locs.length, 0);
  const historyCount = Object.keys(historyByLocation).length;
  let exportStage: PicqerAnalyseExport['exportStage'] = 'warehouses';
  if (locationCount > 0 && historyCount >= locationCount) {
    exportStage = 'complete';
  } else if (locationCount > 0) {
    exportStage = 'locations';
  }

  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    sincedate: ANALYSE_SINCE_DATE,
    untildate: ANALYSE_UNTIL_DATE,
    exportStage,
    warehouses,
    locationsByWarehouse,
    historyByLocation,
    preRangeStockByLocation,
  };
}

export function parseImportPayload(raw: unknown): PicqerAnalyseExport {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid file: expected a JSON object');
  }
  const data = raw as Record<string, unknown>;
  if (data.version !== 1) {
    throw new Error('Unsupported export version');
  }
  if (!Array.isArray(data.warehouses)) {
    throw new Error('Invalid file: missing warehouses array');
  }

  return {
    version: 1,
    exportedAt: typeof data.exportedAt === 'string' ? data.exportedAt : new Date().toISOString(),
    sincedate: typeof data.sincedate === 'string' ? data.sincedate : ANALYSE_SINCE_DATE,
    untildate: typeof data.untildate === 'string' ? data.untildate : ANALYSE_UNTIL_DATE,
    exportStage: (data.exportStage as PicqerAnalyseExport['exportStage']) ?? undefined,
    warehouses: data.warehouses as PicqerWarehouse[],
    locationsByWarehouse: numRecord(
      (data.locationsByWarehouse as Record<string, PicqerLocation[]>) ?? {},
    ),
    historyByLocation: numRecord(
      (data.historyByLocation as Record<string, PicqerLocationStockHistoryEntry[]>) ?? {},
    ),
    preRangeStockByLocation: numRecord(
      (data.preRangeStockByLocation as Record<string, number>) ?? {},
    ),
  };
}

export async function readImportFile(file: File): Promise<PicqerAnalyseExport> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Invalid JSON file');
  }
  return parseImportPayload(parsed);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function downloadJsonExport(payload: PicqerAnalyseExport): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const stage = payload.exportStage ?? 'snapshot';
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  triggerDownload(blob, `picqer-analyse-${stage}-${stamp}.json`);
}

function csvEscape(value: string | number): string {
  const s = String(value);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function downloadMetricsCsv(metrics: MonthlyWarehouseMetric[]): void {
  const headers = [
    'month',
    'warehouse_id',
    'warehouse_name',
    'total_locations',
    'locations_with_any_occupation',
    'avg_occupied_percent',
    'occupied_location_days',
    'days_in_month',
  ];
  const rows = metrics.map((m) =>
    [
      m.month,
      m.warehouseId,
      m.warehouseName,
      m.totalLocations,
      m.usedLocations,
      m.usedPercent,
      m.occupiedLocationDays,
      m.daysInMonth,
    ]
      .map(csvEscape)
      .join(','),
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, `picqer-analyse-metrics-${stamp}.csv`);
}

export function countExportStats(payload: PicqerAnalyseExport): {
  warehouseCount: number;
  locationCount: number;
  historyLocationCount: number;
  exportedAt: string;
} {
  const locationCount = Object.values(payload.locationsByWarehouse).reduce(
    (sum, locs) => sum + locs.length,
    0,
  );
  return {
    warehouseCount: payload.warehouses.length,
    locationCount,
    historyLocationCount: Object.keys(payload.historyByLocation).length,
    exportedAt: payload.exportedAt,
  };
}
